<template>
  <div>
    <div class="primary-background py-3">
      <div class="container">
        <div class="d-flex justify-content-end">
          <ol class="breadcrumb text-align-right">
            <li class="breadcrumb-item flex-grow-1"><a href="/">Home</a></li>
            <li v-if="!loadingWorkshop" class="breadcrumb-item">
              <router-link :to="{ name: 'workshops' }">
                {{ loadingWorkshop ? '...' : 'Workshops' }}
              </router-link>
            </li>
            <li class="breadcrumb-item flex-grow-1 active text-truncate" style="max-width: 280px;">
              {{ loadingWorkshop ? '...' : workshopDetails.name }}
            </li>
          </ol>
        </div>
      </div>
    </div>
    <div class="primary-background mb-4">
      <div class="container pb-5">
        <div v-if="!loadingWorkshop" class="d-flex justify-content-end align-items-center">
          <div v-if="canEditCms">
            <i
              v-b-tooltip.hover
              v-b-modal="'workshopSettingsModal'" class="fa fa-cog edit-icon" title="Quick Edit"
            />
            <a :href="fetchLegacyUrl(`/workshops/${workshopDetails.id}/edit`)">
              <i v-b-tooltip.hover class="fa fa-edit edit-icon" title="Full Edit" />
            </a>
          </div>
          <div ref="parentQuickLinkContainer" class="ml-2">
            <custom-quick-link-section
              :quick-link-list="quickLinkList"
              :show-admin-tools="true"
              :event-type="'workshops'"
              :event-quick-link="workshopDetails.quick_links"
            />
          </div>
        </div>
        <b-alert v-if="!loadingWorkshop && workshopDetails.is_workshop_registration" show variant="info">
          <span class="lyon-display-medium-18">You have registered for this event.</span>
        </b-alert>
        <b-alert v-if="!loadingWorkshop && workshopDetails.is_workshop_registration && workshopDetails.registrant_information" show variant="info">
          <span v-html="workshopDetails.registrant_information" />
        </b-alert>
        <info-block-loader v-if="loadingWorkshop" :property-line="8" info-type="event" />
        <div v-else class="row justify-content-between">
          <div class="col-lg-7 col-xl-4 mb-4">
            <div class="custom-featured-image">
              <img :src="fetchImage(workshopDetails.image_url, 400, 300)" onerror="this.onerror=null;this.src='/img/SLMath/SLMath_default.png';">
              <div class="text-muted regular-12" v-html="workshopDetails.image_caption_name" />
            </div>
          </div>

          <div class="col-lg-5 col-xl-7 my-auto">
            <div v-if="workshopDetails.status" :class="eventStatusType(workshopDetails.status)">
              <span class="regular-14 typo-color-white text-uppercase">
                {{ workshopDetails.status }}
              </span>
            </div>
            <div class="regular-32 mb-2">
              <i
                v-if="!workshopDetails.visible_on_public_site"
                v-b-tooltip.hover.top
                title="SLMath Only"
                class="fas fa-lock"
              /> {{ workshopDetails.name }}
            </div>
            <div class="medium-14 typo-color-blue mb-3">
              <i class="fas fa-envelope" />
              <a :href="`mailto:${workshopDetails.contact}`"> {{ workshopDetails.contact }}</a>
            </div>
            <div class="text-muted regular-16 mb-2">
              <span>{{ moment(workshopDetails.from, 'YYYY-MM-DD HH:mm:ss').format('MMMM Do, YYYY') }}</span>
              <span> - </span>
              <span>{{ moment(workshopDetails.to, 'YYYY-MM-DD HH:mm:ss').format('MMMM Do, YYYY') }}</span>
            </div>
            <div v-if="workshopType !== 'past' && checkNullAndEmptyValues(workshopDetails.registration_cut_off_date)" class="row mb-3 mt-4">
              <div class="col-md-4">
                <span class="text-muted regular-16">Registration Deadline</span>
              </div>
              <div class="col-md-8">
                <div class="regular-16">{{ moment(workshopDetails.registration_cut_off_date, 'YYYY-MM-DD HH:mm:ss').format('MMMM Do, YYYY') }}</div>
              </div>
            </div>
            <div v-if="workshopType !== 'past' && checkNullAndEmptyValues(workshopDetails.funding_cut_off_date)" class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">To Apply For Funding You Must Register By</span>
              </div>
              <div class="col-md-8">
                <div class="regular-16">{{ moment(workshopDetails.funding_cut_off_date, 'YYYY-MM-DD HH:mm:ss').format('MMMM Do, YYYY') }}</div>
              </div>
            </div>
            <div v-if="workshopDetails.parent_program.length > 0" class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">Parent Program</span>
              </div>
              <div class="col-md-8">
                <div
                  v-for="(parentProgram, index) in workshopDetails.parent_program"
                  :key="index"
                  class="mb-1"
                >
                  <router-link :to="{ name: 'programs-details', params: { id: parentProgram.id }}">
                    <span class="regular-16 typo-color-blue">{{ parentProgram.name }}</span>
                  </router-link>
                </div>
              </div>
            </div>
            <div v-if="checkNullAndEmptyValues(workshopDetails.event_location)" class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">Location</span>
              </div>
              <div class="col-md-8">
                <div class="regular-16">{{ workshopDetails.event_location }}</div>
              </div>
            </div>
            <div v-if="canEditCms">
              <!-- <div class="row mb-3">
                  <div class="col-md-4">
                    <span class="text-muted regular-16">Status</span>
                  </div>
                  <div class="col-md-8">
                    <div class="regular-16">{{workshopDetails.status}}</div>
                  </div>
              </div> -->
              <div class="row mb-3">
                <div class="col-md-4">
                  <span class="text-muted regular-16">Categories</span>
                </div>
                <div class="col-md-8">
                  <span v-for="(category, index) in workshopDetails.categories" :key="index" class="regular-16">
                    <span v-if="index < workshopDetails.categories.length - 1">{{ category.name }}, </span>
                    <span v-else>{{ category.name }}</span>
                  </span>
                </div>
              </div>
            </div>

            <div v-if="!workshopDetails.is_workshop_registration && !workshopDetails.secure_registration">
              <span v-if="workshopDetails.redirect_to_external_url && workshopDetails.external_url">
                <a
                  :href="workshopDetails.external_url"
                >
                  <button class="btn btn-primary btn-md mb-3">
                    {{ workshopDetails.registration_button_label ? workshopDetails.registration_button_label : 'Register Now' }}
                  </button>
                </a>
              </span>
              <!-- <span v-else-if="workshopDetails.secure_registration && workshopDetails.reg_link">
                <router-link
                  :to="{ name: 'workshop-registration', params: { id: $route.params.id }, query: { secure: workshopDetails.secure_registration_token }}"
                >
                  <button class="btn btn-primary btn-md mb-3">
                    {{ workshopDetails.registration_button_label ? workshopDetails.registration_button_label : 'Register Now' }}
                  </button>
                </router-link>
              </span> -->
              <span v-else-if="!workshopDetails.is_past && !workshopDetails.registration_date_expired">
                <router-link
                  :to="{ name: 'workshop-registration', params: { id: $route.params.id }}"
                >
                  <button class="btn btn-primary btn-md mb-3">
                    {{ workshopDetails.registration_button_label ? workshopDetails.registration_button_label : 'Register Now' }}
                  </button>
                </router-link>
              </span>
            </div>
            <b-alert v-if="workshopDetails.registration_date_expired && !workshopDetails.is_workshop_registration" show variant="info">
              <span class="lyon-display-medium-18">Registration deadline has passed.</span>
            </b-alert>
          </div>
        </div>
      </div>
      <b-modal
        id="workshopSettingsModal"
        ref="workshopSettingsModal"
        size="xl"
        title="Workshop Quick Edit"
        centered
        hide-footer
        no-enforce-focus
        no-close-on-backdrop
        no-close-on-esc
      >
        <workshop-edit-form
          :modal-data="workshopDataForm"
          :tag-listing="tagListing"
          @updateWorkshopData="updateWorkshopData"
        />
      </b-modal>
    </div>

    <div class="container">
      <tabs-section v-if="!loadingWorkshop" :datas="workshopDetails" />
    </div>
  </div>
</template>

<script>
import TabsSection from "./WorkshopDetailTabsSection.vue"
import mixin from "@/utils/mixin.js"
import InfoBlockLoader from "@/components/Loaders/InfoBlockLoader.vue"
import { mapGetters } from "vuex"
import WorkshopEditForm from "./WorkshopEditForm.vue"
import CustomQuickLinkSection from "@/components/QuickLink/CustomQuickLinkSection.vue"

export default {
	name: "WorkshopDetailsSection",
	components: { TabsSection, InfoBlockLoader, WorkshopEditForm, CustomQuickLinkSection },
	mixins: [mixin],
	metaInfo: {
		title: "Workshops Detail",
	},
	props: ["workshopType"],
	data() {
		return {
			workshopDetails: null,
			workshopDataForm: null,
			loadingWorkshop: true,
			quickLinkList: [],
			tagListing: [],
		}
	},
	computed: {
		...mapGetters(["canEditCms", "isAuthenticated"]),
	},
	watch: {
		isAuthenticated() {
			this.loadWorkshopDetails()
		},
	},
	async created() {
		await this.loadWorkshopDetails()
		await this.loadTagListingData()
	},
	updated() {
		MathJax.typeset()
	},
	methods: {
		updateWorkshopData(isHide) {
			if (isHide) {
				this.$refs.workshopSettingsModal.hide()
			} else {
				this.$refs.workshopSettingsModal.hide()
				this.loadWorkshopDetails()
			}
		},
		loadWorkshopDetails() {
			this.loadingWorkshop = true
			this.$http
				.get(`${process.env.VUE_APP_API_URL}/workshops/${this.$route.params.id}.json`, { validateStatus: () => true })
				.then((res) => {
					if (res.status === 404) {
						this.$router.replace({ name: "home" })
					} else if (!this.checkValidateStatus(res.status)) {
						if (!this.checkExternalEventRedirect(res.data.external_url, res.data.redirect_to_external_url)) {
							this.workshopDetails = res.data
							this.workshopDataForm = { ...res.data }
							this.loadDescScript(res.data.description)
							if (res.data.quick_link_hdr_id !== null) this.quickLinkList = res.data.menu
							this.loadingWorkshop = false
						}
					}
				})
		},
		loadTagListingData() {
			this.$http.get(`${process.env.VUE_APP_API_URL}/workshops/tag_listing`).then((res) => {
				if (res !== null) this.tagListing = res.data
			})
		},
	},
}
</script>
